import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    // Initial setup needs to first reset to clean up Liquid-rendered items
    this.#resetContent();
    this.#addRepeatedItems(this.#calculateNumberOfCopies());
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  // UPDATED: Use a more linear calculation to avoid progressive slowdown
  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')); // Use 25 as a base speed
    const { content } = this.refs;
    
    // The total width of the *first* content block (all repeated items + gaps)
    const contentWidth = content.offsetWidth; 

    // Calculate speed based on content width. Higher width = higher duration = slower speed
    // Dividing by a fixed factor (like 250) gives a linear speed: 10 seconds per 2500px, etc.
    // The speedFactor can scale this value up or down.
    const baseSpeed = contentWidth / 250; 
    const speed = baseSpeed * (1 + (speedFactor / 50)); // Adjust speed factor to control the range

    // Ensure we return a positive duration
    return Math.max(speed, 5); 
  }

  // UPDATED: Handle resize by rebuilding content only if copy count changes
  #handleResize = debounce(() => {
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    // Get the *current* count of unique items dynamically added to the content wrapper
    const currentNumberOfCopies = this.refs.content.children.length; 

    if (newNumberOfCopies !== currentNumberOfCopies) {
      
      this.#resetContent(); 
      this.#addRepeatedItems(newNumberOfCopies);
      this.#duplicateContent(); 
      this.#restartAnimation(); 
    }

    // Always update speed, even on small, benign resizes (like iOS scroll)
    // The animation will NOT restart unless the copy count changes.
    this.#setSpeed();

  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  // NEW: Function to remove all dynamically added content
  #resetContent() {
    // 1. Remove the cloned content (the second main block)
    this.clonedContent?.remove(); 

    // 2. Remove all dynamically added repeated items inside the primary content block
    const { content } = this.refs;
    
    // Assumes marqueeItems is an array of ONE item in the Liquid file for the first loop
    // Remove all children except the first one (the original unique content block)
    while (content.children.length > 1) {
        content.lastElementChild?.remove();
    }
  }

  // UPDATED: Adjust logic to build up to the required number of copies
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    // Use the first item in the marqueeItems collection as the source for cloning
    const sourceItem = marqueeItems[0]; 
    if (!sourceItem) return;

    // Start from 1, since the first item is already in the content block
    for (let i = 1; i < numberOfCopies; i++) {
      const clone = sourceItem.cloneNode(true);
      content.appendChild(clone);
    }
  }

  // REMOVED: #removeRepeatedItems is no longer needed since #resetContent handles cleanup

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;

    // Calculate how many copies of the unique item are needed to span the marquee width
    // Add 1 extra copy to ensure continuous looping/gap coverage
    const copiesNeeded = marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth) + 1; 
    
    return copiesNeeded;
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}